import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail, MapPin, Mic, Phone, RefreshCw, Square, Tractor } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { toast } from 'sonner';

const CHAMPS = [
  ['caractere', 'Caractère / relation'],
  ['points_faibles', 'Points faibles / vigilance'],
  ['besoins', 'Besoins exprimés'],
  ['opportunites', 'Opportunités CASIA'],
  ['notes', 'Notes libres'],
];

function ChampDicte({ fiche, champ, label, valeur, setValeur, onSave, dictee }) {
  const actif = dictee.cible === `${fiche.id}:${champ}`;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-stone-600">{label}</span>
        <Button size="icon" variant={actif ? 'destructive' : 'ghost'} className="h-7 w-7"
          onClick={() => dictee.basculer(fiche.id, champ)} data-testid={`fiche-mic-${champ}-${fiche.id}`}>
          {actif && dictee.etat === 'transcribing' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : actif ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <Textarea rows={2} value={valeur} onChange={(e) => setValeur(e.target.value)} onBlur={onSave}
        className="text-sm" data-testid={`fiche-${champ}-${fiche.id}`} />
    </div>
  );
}

function FicheCarte({ fiche, onMaj, dictee }) {
  const [vals, setVals] = useState(() => Object.fromEntries(CHAMPS.map(([k]) => [k, fiche[k] || ''])));
  useEffect(() => { setVals(Object.fromEntries(CHAMPS.map(([k]) => [k, fiche[k] || '']))); }, [fiche]);
  const sauver = (k) => () => { if ((fiche[k] || '') !== vals[k]) onMaj(fiche.id, { [k]: vals[k] }); };
  const t = fiche.travaux_eta || {};
  return (
    <Card data-testid={`fiche-tournee-${fiche.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="font-semibold text-stone-900">{fiche.client_nom}</div>
            <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {fiche.commune && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{fiche.commune}</span>}
              {fiche.telephone && <a href={`tel:${fiche.telephone}`} className="flex items-center gap-1"><Phone className="w-3 h-3" />{fiche.telephone}</a>}
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{fiche.email}</span>
            </div>
          </div>
          <div className="flex gap-1 text-xs">
            {fiche.a_repondu && <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">A répondu</span>}
            <span className={`px-2 py-0.5 rounded ${fiche.ouvertures >= 2 ? 'bg-orange-100 text-orange-800' : 'bg-stone-100 text-stone-600'}`}
              data-testid={`fiche-ouvertures-${fiche.id}`}>Mail ouvert {fiche.ouvertures || 0} fois</span>
            {fiche.bio && <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Bio</span>}
          </div>
        </div>
        <div className="text-xs text-stone-600 bg-stone-50 rounded p-2 flex gap-2">
          <Tractor className="w-4 h-4 shrink-0 text-stone-400" />
          <span>{t.nb ? `${t.nb} travaux ETA (dernier ${t.derniere}) — ${t.operations?.join(', ')}${t.cultures?.length ? ` — ${t.cultures.join(', ')}` : ''}` : 'Aucun travail ETA enregistré'}</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {CHAMPS.map(([k, label]) => (
            <ChampDicte key={k} fiche={fiche} champ={k} label={label} valeur={vals[k]} dictee={dictee}
              setValeur={(v) => setVals((p) => ({ ...p, [k]: v }))} onSave={sauver(k)} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function FichesTournee() {
  const [fiches, setFiches] = useState([]);
  const [charge, setCharge] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [filtre, setFiltre] = useState('');
  const [cible, setCible] = useState(null);

  const charger = useCallback(async () => {
    const r = await api.get('/fiches-tournee');
    setFiches(r.data.fiches || []);
    setCharge(true);
  }, []);
  useEffect(() => { charger().catch(() => toast.error('Fiches indisponibles')); }, [charger]);

  const maj = async (id, champs) => {
    try {
      const r = await api.patch(`/fiches-tournee/${id}`, champs);
      setFiches((l) => l.map((f) => (f.id === id ? r.data : f)));
      toast.success('Fiche enregistrée');
    } catch { toast.error("Échec de l'enregistrement"); }
  };

  const voix = useVoiceRecorder({
    onText: (texte) => {
      const [id, champ] = (cible || '').split(':');
      const f = fiches.find((x) => x.id === id);
      if (f) maj(id, { [champ]: f[champ] ? `${f[champ]}\n${texte}` : texte });
      setCible(null);
    },
    onError: (m) => { toast.error(m); setCible(null); },
  });
  const dictee = {
    cible, etat: voix.etat,
    basculer: (id, champ) => {
      if (voix.etat === 'listening') { voix.stop(); return; }
      setCible(`${id}:${champ}`);
      voix.start();
    },
  };

  const generer = async () => {
    setChargement(true);
    try {
      const r = await api.post('/fiches-tournee/generer');
      toast.success(`${r.data.total} fiches à jour (${r.data.nouvelles} nouvelles)`);
      await charger();
    } catch { toast.error('Préparation impossible'); }
    setChargement(false);
  };

  const visibles = fiches.filter((f) => `${f.client_nom} ${f.commune || ''}`.toLowerCase().includes(filtre.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="fiches-tournee">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-lg font-semibold">Tournée de Julien (CASIA) — fiches clients</h2>
          <p className="text-sm text-stone-500">Valentine pré-remplit, tu complètes au micro ou en disant à Valentine « sur la fiche Mourchon, note que… ».</p>
        </div>
        <Input placeholder="Chercher un client…" value={filtre} onChange={(e) => setFiltre(e.target.value)} className="w-56" data-testid="fiches-filtre" />
        <Button onClick={generer} disabled={chargement} data-testid="fiches-generer-btn">
          {chargement ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {fiches.length ? 'Mettre à jour' : 'Préparer les fiches'}
        </Button>
      </div>
      {!charge && <Loader2 className="w-5 h-5 animate-spin text-stone-400" data-testid="fiches-chargement" />}
      {charge && !fiches.length && <p className="text-sm text-stone-500" data-testid="fiches-vide">Aucune fiche : clique sur « Préparer les fiches ».</p>}
      {visibles.map((f) => <FicheCarte key={f.id} fiche={f} onMaj={maj} dictee={dictee} />)}
    </div>
  );
}
